import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { endpoints, type Product, type RecipeSummary } from '../api/endpoints';
import { ApiException } from '../api/client';
import { RecipeCard } from '../components/RecipeCard';
import { Skeleton, ErrorState, EmptyState } from '../components/states';

export function CookFromProductScreen() {
  const nav = useNavigate();
  const [search, setSearch] = useState('');
  const [results, setResults] = useState<Product[]>([]);
  const [selected, setSelected] = useState<Product | null>(null);
  const [items, setItems] = useState<RecipeSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    const q = search.trim();
    if (q === '') {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setResults([]);
      return;
    }
    timer.current = setTimeout(() => {
      endpoints.getProducts({ search: q, limit: 20 }).then(setResults).catch(() => setResults([]));
    }, 300);
    return () => {
      if (timer.current) clearTimeout(timer.current);
    };
  }, [search]);

  function pick(p: Product) {
    setSelected(p);
    setResults([]);
    setSearch('');
    setItems(null);
    setError(null);
    void endpoints.getRecipesByProduct(p.id!).then(
      (r) => setItems(r.items),
      (e) => setError(e instanceof ApiException ? e.body.message : 'Не удалось загрузить'),
    );
  }

  return (
    <div className="px-4 pt-6 pb-[calc(2rem+env(safe-area-inset-bottom))]">
      <div className="mb-4 flex items-center gap-3">
        <button onClick={() => nav(-1)} aria-label="Назад" className="text-xl text-ink/60">←</button>
        <h1 className="text-2xl font-bold text-ink">Что приготовить из…</h1>
      </div>

      {selected ? (
        <button
          onClick={() => { setSelected(null); setItems(null); setError(null); }}
          className="mb-4 inline-flex items-center gap-2 rounded-full bg-primary-50 px-3 py-1.5 text-sm text-primary-700"
        >
          {selected.nameRu} ✕
        </button>
      ) : (
        <input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Введите продукт"
          aria-label="Поиск продукта"
          className="mb-4 w-full rounded-xl border border-black/10 px-4 py-3"
        />
      )}

      {!selected && results.length > 0 && (
        <div className="mb-4 flex flex-wrap gap-2">
          {results.map((p) => (
            <button key={p.id} onClick={() => pick(p)} className="rounded-full bg-white px-3 py-1.5 text-sm shadow-sm">
              {p.nameRu}
            </button>
          ))}
        </div>
      )}

      {selected && (
        error ? (
          <ErrorState message={error} onRetry={() => pick(selected)} />
        ) : items === null ? (
          <div className="space-y-4">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-44 w-full" />)}</div>
        ) : items.length === 0 ? (
          <EmptyState title="Ничего не нашли" hint="Попробуйте другой продукт." />
        ) : (
          <div className="stagger space-y-4">
            {items.map((r) => (
              <RecipeCard key={r.id} title={r.title ?? ''} imageUrl={r.imageUrl}
                cookTimeMin={r.cookTimeMin} servings={r.servings} onClick={() => nav(`/recipe/${r.id}`)} />
            ))}
          </div>
        )
      )}
    </div>
  );
}
